/**
 * Context Panel Handlers — TASK_OUTPUT_REGISTERED and AGENT_TODO persistence.
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import { useTaskStore } from "../taskStore";
import { useFileUrlStore } from "../fileUrlStore";
import type { TaskOutput, TaskTodoGroup } from "../types";
import { toCanonicalPath } from "@/lib/file-paths";
import type { ClientPayloadOf } from "../socketService";

export function registerContextPanelHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;

  // NOT PayloadOf: TaskOutputRegisteredPayloadSchema embeds a TaskOutput schema with no
  // filePath / mediaType, which this handler reads and the TaskOutput TYPE declares —
  // the schema contradicts the wire (s2-07b finding). Inline until the schema is fixed.
  const handleOutputRegistered = (data: {
    taskId: string;
    outputs: TaskOutput[];
  }) => {
    if (!data.taskId || !data.outputs?.length) return;
    // Canonicalise filePath on every incoming output so downstream lookup sites
    // (chip click, FileOutputCard) can compare with === instead of running
    // ad-hoc normalisation. See apps/web/src/lib/file-paths.ts.
    const canonicalOutputs = data.outputs.map((o) => ({
      ...o,
      filePath: toCanonicalPath(o.filePath ?? `files/${o.title}`),
    }));
    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(data.taskId);
      if (!task) return;
      const existingUrls = new Set(task.outputs.map((o) => o.url));
      const unique = canonicalOutputs.filter((o) => !existingUrls.has(o.url));
      if (unique.length > 0) {
        task.outputs = [...unique, ...task.outputs];
      }
    });

    // Populate fileUrlStore so FileOutputCard can resolve CDN URLs
    const store = useFileUrlStore.getState();
    for (const o of canonicalOutputs) {
      if (o.url && o.title) {
        store.set(o.filePath, { url: o.url, mediaType: o.mediaType || "" });
      }
    }

    // Previously called `onFileGenerated` here, which auto-OPENED the Computer
    // panel on every file batch. That triggered an unwanted side effect: when
    // the user navigated to ANY old task with persisted outputs, the resumed
    // MESSAGE_SYNC replayed historical outputs as TASK_OUTPUT_REGISTERED
    // events, and Computer popped open uninvited. Removed — Computer now
    // opens only on explicit user action (chip click / Computer toggle).
  };

  const handleAgentTodo = (data: ClientPayloadOf<typeof EVENTS.AGENT_TODO>) => {
    // taskId is the relay's routing tag (RelayForwardTags), narrowed once here
    // because the guard does not carry into the _mutateTasks closure.
    const taskId = data.taskId;
    if (!taskId || !data.todos) return;
    taskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;
      // §3.11, D10 — the turn to write is the HIGHEST one on the array, not
      // whatever sits at index 0. The array is newest-first only while the tab
      // stays open (this handler prepends); the sync hydrates it in the
      // server's order, oldest first.
      const latestTurnIndex = task.todos.reduce(
        (max, g) => (g.turnIndex > max ? g.turnIndex : max),
        0
      );
      const group: TaskTodoGroup = {
        turnIndex: latestTurnIndex,
        todos: data.todos.map((t) => ({
          content: t.content,
          status: t.status as "pending" | "in_progress" | "completed",
          activeForm: t.activeForm,
          // Spread, never a plain assignment: an unheld row must have NO `owner`
          // key at all, not a key holding undefined, so it is shaped exactly as a
          // row written before the board existed.
          ...(t.owner ? { owner: t.owner } : {}),
        })),
        createdAt: new Date().toISOString(),
      };
      // Replace that turn's group WHEREVER it sits, else prepend a new one.
      const at = task.todos.findIndex((g) => g.turnIndex === group.turnIndex);
      if (at >= 0) {
        task.todos[at] = group;
      } else {
        task.todos.unshift(group);
      }
    });
  };

  socket.on(EVENTS.TASK_OUTPUT_REGISTERED, handleOutputRegistered);
  socket.on(EVENTS.AGENT_TODO, handleAgentTodo);

  return () => {
    socket.off(EVENTS.TASK_OUTPUT_REGISTERED, handleOutputRegistered);
    socket.off(EVENTS.AGENT_TODO, handleAgentTodo);
  };
}
